import * as React from 'react'

import { ActionItem, Box, Button, Text, Title } from '@factorialco/deprecated-design-system'
import { ProcessesTaskConfigurationSendDocumentTemplate } from 'generated/resources/graphql'

import { BoxList } from 'modules/core/components/BoxList'
import DocumentType from 'modules/core/components/DocumentType'
import TextField from 'modules/core/components/TextField'
import { useForm } from 'modules/core/hooks/useForm'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import Tags from 'modules/documents/components/DocumentTemplates/Tags'
import type { DocumentTemplate } from 'modules/documents/hooks/useGetDocumentTemplates'
import { useUpdateDocumentTemplate } from 'modules/documents/hooks/useUpdateDocumentTemplate'

import SettingsButton from './SettingsButton'
import WorkflowsIntegration from './WorkflowsIntegration'

interface Props {
  documentTemplate: DocumentTemplate
  taskConfigurations?: ProcessesTaskConfigurationSendDocumentTemplate[]
}

interface TemplateFormValues {
  name: string
}

const BasicInfo = ({ documentTemplate, taskConfigurations }: Props) => {
  const [showSubmit, setShowSubmit] = React.useState<boolean>(false)
  const { updateDocumentTemplate } = useUpdateDocumentTemplate()

  const isFillable = documentTemplate.fillable
  const hasVariables = !!documentTemplate.values?.length

  const form = useForm<TemplateFormValues>({
    defaultValues: {
      name: documentTemplate.name,
    },
    onSubmit: async (data) => {
      const result = await updateDocumentTemplate({
        id: documentTemplate.id!,
        name: data.name,
      })

      const handled = handleResult(result, (formErrors) => {
        form.setErrors({ name: formErrors.name })
      })

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
      }

      return handled
    },
  })

  const description = () => {
    if (isFillable && hasVariables) {
      return i18n.t('documents.document_templates.basic_info.subtitle_fillable_and_variables')
    }

    if (isFillable) {
      return i18n.t('documents.document_templates.basic_info.subtitle_fillable')
    }

    if (hasVariables) {
      return i18n.t('documents.document_templates.basic_info.subtitle_variables')
    }

    return i18n.t('documents.document_templates.basic_info.subtitle')
  }

  const onChange = (value: string) => {
    form.setValues({ ...form.values, name: value })
    setShowSubmit(form.isValid)
  }

  const BasicInfoBody = (
    <>
      <form onSubmit={form.handleSubmit}>
        <Box gap="s6">
          <TextField
            label={i18n.t('documents.document_templates.basic_info.input_label')}
            mandatory
            loading={form.isSubmitting}
            value={form.values.name}
            onChange={(value) => onChange(value)}
            error={form.errors.name}
          />
          {showSubmit && (
            <Box alignItems="flexEnd">
              <Button disabled={form.isSubmitting || !form.isValid} submit>
                {i18n.t('documents.document_templates.basic_info.submit')}
              </Button>
            </Box>
          )}
        </Box>
      </form>
      <Box gap="s8">
        <Text.Normal size="200" color="grey1000">
          {i18n.t('documents.document_templates.basic_info.original_file')}
        </Text.Normal>
        <BoxList>
          <ActionItem whiteDisabled>
            <Box width="full">
              <Box
                flexDirection="row"
                justifyContent="spaceBetween"
                alignItems="center"
                overflowX="hidden"
                gap="s8"
              >
                <Box
                  flexDirection="row"
                  alignItems="center"
                  gap="s8"
                  overflowX="hidden"
                  paddingRight="s16"
                >
                  {documentTemplate.mimetype && (
                    <DocumentType mimetype={documentTemplate.mimetype} />
                  )}
                  <Box
                    aria-label={i18n.t('documents.document_templates.basic_info.original_file')}
                    overflowX="hidden"
                  >
                    <Text color="grey1000" weight="medium" size="300" ellipsis>
                      {documentTemplate.filename}
                    </Text>
                  </Box>
                </Box>
                <SettingsButton
                  documentTemplate={documentTemplate}
                  taskConfigurations={taskConfigurations}
                />
              </Box>
            </Box>
          </ActionItem>
        </BoxList>
      </Box>
    </>
  )

  return (
    <Box flexDirection="row" justifyContent="spaceBetween" paddingX="s48" paddingBottom="s48">
      <Box gap="s6">
        <Title
          variant="500"
          illustration="template"
          title={i18n.t('documents.document_templates.basic_info.title')}
          description={description()}
        />
        <Tags documentTemplate={documentTemplate} />
        {taskConfigurations && <WorkflowsIntegration taskConfigurations={taskConfigurations} />}
      </Box>
      <Box
        style={{ width: '447px', minWidth: '447px' }}
        background="grey200"
        padding="s24"
        gap="s8"
        borderRadius={{
          all: 'abs008',
        }}
      >
        {BasicInfoBody}
      </Box>
    </Box>
  )
}

export default BasicInfo
